import React from "react";

import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    CssBaseline,
    Divider,
    Grid,
    Paper,
    Stack,
    ThemeProvider,
    Typography,
    createTheme,
} from "@mui/material";

import {
    ArrowForward,
    AutoStories,
    ChildCare,
    EmojiEmotions,
    Explore,
    Headphones,
    Lightbulb,
    MenuBook,
    Palette,
    Psychology,
    School,
    SmartToy,
    Star,
    Toys,
    TrendingUp,
} from "@mui/icons-material";

import Navbar from "../Navbar";
import Footer from "../HomeFooter";

// ======================================================
// THEME
// ======================================================

const theme = createTheme({
    palette: {
        primary: {
            main: "#1976d2",
        },
        secondary: {
            main: "#ffb300",
        },
        background: {
            default: "#f8fbff",
        },
    },

    typography: {
        fontFamily:
            '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',

        h1: {
            fontWeight: 800,
        },

        h2: {
            fontWeight: 800,
        },

        h3: {
            fontWeight: 700,
        },

        h4: {
            fontWeight: 700,
        },

        h5: {
            fontWeight: 700,
        },
    },

    shape: {
        borderRadius: 16,
    },
});

// ======================================================
// FEATURES
// ======================================================

const features = [
    {
        icon: <Headphones />,
        title: "Kids’ Rhymes",
        description:
            "Fun and engaging rhymes that help children enjoy learning while supporting listening, memory, language, and recognition skills.",
    },
    {
        icon: <AutoStories />,
        title: "Kids’ Stories",
        description:
            "Interesting and child-friendly stories designed to encourage imagination, creativity, and curiosity.",
    },
    {
        icon: <School />,
        title: "Educational Tutorials",
        description:
            "Simple and engaging tutorials that introduce children to learning concepts in an easy-to-understand format.",
    },
    {
        icon: <Toys />,
        title: "Learn Through Play",
        description:
            "A combination of learning and entertainment that encourages children to learn while having fun.",
    },
    {
        icon: <Palette />,
        title: "Engaging Digital Content",
        description:
            "Creative and visually appealing content designed to attract and maintain children’s attention.",
    },
    {
        icon: <ChildCare />,
        title: "Child-Friendly Experience",
        description:
            "An interface and content approach designed around the interests and learning needs of young children.",
    },
];

// ======================================================
// DEVELOPMENT AREAS
// ======================================================

const developmentAreas = [
    {
        icon: <Palette />,
        title: "Creativity",
    },
    {
        icon: <Psychology />,
        title: "Imagination",
    },
    {
        icon: <School />,
        title: "Language & Communication",
    },
    {
        icon: <Headphones />,
        title: "Listening Skills",
    },
    {
        icon: <Lightbulb />,
        title: "Memory & Recognition",
    },
    {
        icon: <Explore />,
        title: "Curiosity",
    },
    {
        icon: <TrendingUp />,
        title: "Exploration",
    },
    {
        icon: <SmartToy />,
        title: "Independent Learning",
    },
];

// ======================================================
// PROJECT DETAILS
// ======================================================

const projectDetails = [
    {
        label: "Product Name",
        value: "iMPULZ Kids",
    },
    {
        label: "Platform",
        value: "Mobile Application",
    },
    {
        label: "Domain",
        value: "Kids Learning & Entertainment",
    },
    {
        label: "Target Audience",
        value: "Children",
    },
    {
        label: "Content",
        value: "Rhymes, Stories & Tutorials",
    },
    {
        label: "Primary Focus",
        value: "Interactive Learning & Play",
    },
    {
        label: "Experience",
        value: "Child-Friendly & Engaging",
    },
];

// ======================================================
// FEATURE CARD
// ======================================================

function FeatureCard({ icon, title, description }) {
    return (
        <Card
            elevation={0}
            sx={{
                height: "100%",
                border: "1px solid #e3edf7",
                borderRadius: 4,
                background: "#fff",
                transition: "all 0.3s ease",

                "&:hover": {
                    transform: "translateY(-7px)",
                    boxShadow:
                        "0 15px 35px rgba(25, 118, 210, 0.12)",
                    borderColor: "#90caf9",
                },
            }}
        >
            <CardContent sx={{ p: 3.5 }}>
                <Box
                    sx={{
                        width: 58,
                        height: 58,
                        borderRadius: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background:
                            "linear-gradient(135deg, #1976d2, #42a5f5)",
                        color: "#fff",
                        mb: 2.5,
                    }}
                >
                    {icon}
                </Box>

                <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ mb: 1.2 }}
                >
                    {title}
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.8 }}
                >
                    {description}
                </Typography>
            </CardContent>
        </Card>
    );
}

// ======================================================
// DEVELOPMENT CARD
// ======================================================

function DevelopmentCard({ icon, title }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.2,
                borderRadius: 3,
                border: "1px solid #e3edf7",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                background: "#fff",
                transition: "all 0.25s ease",

                "&:hover": {
                    transform: "translateY(-3px)",
                    borderColor: "#90caf9",
                    boxShadow:
                        "0 8px 25px rgba(25,118,210,0.1)",
                },
            }}
        >
            <Box
                sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 2.5,
                    background: "#e3f2fd",
                    color: "#1976d2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>

            <Typography
                variant="body2"
                fontWeight={600}
            >
                {title}
            </Typography>
        </Paper>
    );
}

// ======================================================
// IMPULZ KIDS COMPONENT
// ======================================================

export default function ImpulzKids() {
    return (
        <>
            <Navbar />

            <ThemeProvider theme={theme}>
                <CssBaseline />

                {/* ==================================================
                    HERO
                ================================================== */}

                <Box
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        background:
                            "linear-gradient(135deg, #0d47a1 0%, #1976d2 50%, #42a5f5 100%)",
                        color: "#fff",
                        py: {
                            xs: 9,
                            md: 12,
                        },
                    }}
                >
                    {/* Decorative Circle */}

                    <Box
                        sx={{
                            position: "absolute",
                            width: 280,
                            height: 280,
                            borderRadius: "50%",
                            background:
                                "rgba(255,255,255,0.07)",
                            top: -100,
                            right: -70,
                        }}
                    />

                    <Box
                        sx={{
                            position: "absolute",
                            width: 180,
                            height: 180,
                            borderRadius: "50%",
                            background:
                                "rgba(255,193,7,0.12)",
                            bottom: -70,
                            left: -40,
                        }}
                    />

                    <Container
                        maxWidth="lg"
                        sx={{
                            position: "relative",
                            zIndex: 1,
                        }}
                    >
                        <Grid
                            container
                            spacing={6}
                            alignItems="center"
                        >
                            {/* HERO CONTENT */}

                            <Grid
                                item
                                xs={12}
                                md={7}
                            >
                                <Chip
                                    icon={<ChildCare />}
                                    label="Kids Learning & Entertainment"
                                    sx={{
                                        mb: 2.5,
                                        color: "#fff",
                                        background:
                                            "rgba(255,255,255,0.14)",
                                        border:
                                            "1px solid rgba(255,255,255,0.25)",
                                        fontWeight: 600,

                                        "& .MuiChip-icon": {
                                            color: "#fff",
                                        },
                                    }}
                                />

                                <Typography
                                    variant="h1"
                                    sx={{
                                        fontSize: {
                                            xs: "2.8rem",
                                            sm: "3.5rem",
                                            md: "4.5rem",
                                        },
                                        lineHeight: 1.05,
                                        mb: 2,
                                    }}
                                >
                                    iMPULZ Kids
                                </Typography>

                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontSize: {
                                            xs: "1.45rem",
                                            md: "2rem",
                                        },
                                        fontWeight: 500,
                                        lineHeight: 1.4,
                                        mb: 3,
                                        maxWidth: 700,
                                    }}
                                >
                                    Interactive Learning &{" "}
                                    Entertainment Platform
                                    for Kids
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        maxWidth: 680,
                                        opacity: 0.9,
                                        lineHeight: 1.8,
                                        mb: 4,
                                        fontSize: "1.05rem",
                                    }}
                                >
                                    iMPULZ Kids is a child-focused
                                    mobile application designed
                                    to make learning fun,
                                    engaging, and interactive.
                                    The platform combines
                                    education and entertainment
                                    through kids’ rhymes,
                                    stories, tutorials, and
                                    engaging digital content.
                                </Typography>

                                {/* <Stack
                                    direction={{
                                        xs: "column",
                                        sm: "row",
                                    }}
                                    spacing={2}
                                >
                                    <Button
                                        variant="contained"
                                        size="large"
                                        endIcon={<ArrowForward />}
                                        sx={{
                                            background: "#ffb300",
                                            color: "#172b4d",
                                            fontWeight: 700,
                                            borderRadius: 50,
                                            px: 4,
                                            py: 1.4,

                                            "&:hover": {
                                                background:
                                                    "#ffa000",
                                            },
                                        }}
                                    >
                                        Explore iMPULZ Kids
                                    </Button>

                                    <Button
                                        variant="outlined"
                                        size="large"
                                        sx={{
                                            color: "#fff",
                                            borderColor:
                                                "rgba(255,255,255,0.6)",
                                            borderRadius: 50,
                                            px: 4,
                                            py: 1.4,

                                            "&:hover": {
                                                borderColor: "#fff",
                                                background:
                                                    "rgba(255,255,255,0.08)",
                                            },
                                        }}
                                    >
                                        Learn More
                                    </Button>
                                </Stack> */}
                            </Grid>

                            {/* ==================================================
                                HERO VISUAL - INCREASED WIDTH
                            ================================================== */}

                            <Grid
                                item
                                xs={12}
                                md={5}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: {
                                            xs: "center",
                                            md: "flex-end",
                                        },
                                    }}
                                >
                                    <Paper
                                        elevation={15}
                                        sx={{
                                            /*
                                             * INCREASED WIDTH
                                             */
                                            width: {
                                                xs: 310,
                                                sm: 360,
                                                md: 390,
                                            },

                                            minHeight: {
                                                xs: 410,
                                                sm: 430,
                                                md: 450,
                                            },

                                            borderRadius: 7,

                                            p: {
                                                xs: 3,
                                                sm: 3.5,
                                                md: 4,
                                            },

                                            background:
                                                "linear-gradient(145deg, #ffffff, #f3f9ff)",

                                            position: "relative",
                                            overflow: "hidden",

                                            boxSizing: "border-box",
                                        }}
                                    >
                                        {/* Decorative circle */}

                                        <Box
                                            sx={{
                                                position:
                                                    "absolute",
                                                width: 130,
                                                height: 130,
                                                borderRadius:
                                                    "50%",
                                                background:
                                                    "#fff3cd",
                                                top: -45,
                                                right: -35,
                                            }}
                                        />

                                        {/* Emoji */}

                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent:
                                                    "center",
                                                mb: 2,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 90,
                                                    height: 90,
                                                    borderRadius:
                                                        "50%",
                                                    background:
                                                        "linear-gradient(135deg, #ffca28, #ffb300)",
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    justifyContent:
                                                        "center",
                                                    color: "#fff",
                                                    boxShadow:
                                                        "0 10px 25px rgba(255,179,0,0.3)",
                                                }}
                                            >
                                                <EmojiEmotions
                                                    sx={{
                                                        fontSize: 52,
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* Title */}

                                        <Typography
                                            variant="h5"
                                            fontWeight={800}
                                            color="primary.main"
                                            textAlign="center"
                                        >
                                            iMPULZ Kids
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            textAlign="center"
                                            sx={{
                                                mt: 1,
                                                mb: 3,
                                            }}
                                        >
                                            Learn • Play • Explore
                                        </Typography>

                                        {/* Features */}

                                        <Stack
                                            spacing={1.5}
                                            sx={{ mt: 1 }}
                                        >
                                            {[
                                                {
                                                    icon: (
                                                        <Headphones />
                                                    ),
                                                    text: "Fun Rhymes",
                                                },
                                                {
                                                    icon: (
                                                        <MenuBook />
                                                    ),
                                                    text: "Amazing Stories",
                                                },
                                                {
                                                    icon: (
                                                        <School />
                                                    ),
                                                    text: "Learning Tutorials",
                                                },
                                                {
                                                    icon: (
                                                        <Toys />
                                                    ),
                                                    text: "Learn Through Play",
                                                },
                                            ].map((item) => (
                                                <Paper
                                                    key={
                                                        item.text
                                                    }
                                                    elevation={0}
                                                    sx={{
                                                        p: 1.5,
                                                        minHeight: 48,
                                                        borderRadius: 3,
                                                        background:
                                                            "#f3f8fd",
                                                        display:
                                                            "flex",
                                                        alignItems:
                                                            "center",
                                                        gap: 1.5,
                                                        width: "100%",
                                                        boxSizing:
                                                            "border-box",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            color:
                                                                "primary.main",
                                                            display:
                                                                "flex",
                                                        }}
                                                    >
                                                        {
                                                            item.icon
                                                        }
                                                    </Box>

                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={
                                                            600
                                                        }
                                                    >
                                                        {
                                                            item.text
                                                        }
                                                    </Typography>
                                                </Paper>
                                            ))}
                                        </Stack>
                                    </Paper>
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>

                {/* ==================================================
                    PRODUCT OVERVIEW
                ================================================== */}

                <Box
                    sx={{
                        py: {
                            xs: 8,
                            md: 10,
                        },
                        background: "#fff",
                    }}
                >
                    <Container maxWidth="lg">
                        <Grid
                            container
                            spacing={6}
                            alignItems="center"
                        >
                            <Grid
                                item
                                xs={12}
                                md={6}
                            >
                                <Typography
                                    variant="overline"
                                    color="primary.main"
                                    fontWeight={700}
                                    letterSpacing={2}
                                >
                                    Product Overview
                                </Typography>

                                <Typography
                                    variant="h3"
                                    sx={{
                                        mt: 1,
                                        mb: 3,
                                        fontSize: {
                                            xs: "2rem",
                                            md: "2.8rem",
                                        },
                                    }}
                                >
                                    Making learning fun,
                                    engaging, and interactive
                                </Typography>

                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.9,
                                        mb: 2,
                                    }}
                                >
                                    Children learn naturally
                                    through stories, songs,
                                    visuals, activities, and
                                    play. iMPULZ Kids brings
                                    these elements together
                                    in a single digital platform
                                    to create an enjoyable
                                    learning environment for
                                    young learners.
                                </Typography>

                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.9,
                                    }}
                                >
                                    The application provides
                                    engaging content that
                                    encourages children to
                                    watch, listen, explore,
                                    and learn at their own pace.
                                    From entertaining rhymes
                                    and imaginative stories to
                                    educational tutorials,
                                    iMPULZ Kids aims to make
                                    learning a more enjoyable
                                    and interactive experience.
                                </Typography>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: {
                                            xs: 3,
                                            md: 5,
                                        },
                                        borderRadius: 5,
                                        background:
                                            "linear-gradient(135deg, #e3f2fd, #f8fbff)",
                                        border:
                                            "1px solid #d8ebfa",
                                    }}
                                >
                                    <Stack spacing={3}>
                                        {[
                                            {
                                                icon: (
                                                    <Headphones />
                                                ),
                                                title: "Listen",
                                                text: "Enjoy rhymes, songs and audio content.",
                                            },
                                            {
                                                icon: (
                                                    <AutoStories />
                                                ),
                                                title: "Watch",
                                                text: "Discover imaginative stories and tutorials.",
                                            },
                                            {
                                                icon: (
                                                    <Explore />
                                                ),
                                                title: "Explore",
                                                text: "Discover new concepts through interactive content.",
                                            },
                                            {
                                                icon: (
                                                    <Toys />
                                                ),
                                                title: "Play & Learn",
                                                text: "Learn naturally through fun and engaging experiences.",
                                            },
                                        ].map((item) => (
                                            <Stack
                                                key={
                                                    item.title
                                                }
                                                direction="row"
                                                spacing={2}
                                                alignItems="center"
                                            >
                                                <Box
                                                    sx={{
                                                        width: 52,
                                                        height: 52,
                                                        borderRadius: 3,
                                                        background:
                                                            "#fff",
                                                        color:
                                                            "primary.main",
                                                        display:
                                                            "flex",
                                                        alignItems:
                                                            "center",
                                                        justifyContent:
                                                            "center",
                                                        boxShadow:
                                                            "0 5px 15px rgba(25,118,210,0.08)",
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {
                                                        item.icon
                                                    }
                                                </Box>

                                                <Box>
                                                    <Typography
                                                        variant="h6"
                                                        fontWeight={
                                                            700
                                                        }
                                                    >
                                                        {
                                                            item.title
                                                        }
                                                    </Typography>

                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {
                                                            item.text
                                                        }
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>

                {/* ==================================================
                    THE CHALLENGE
                ================================================== */}

                <Box
                    sx={{
                        py: {
                            xs: 8,
                            md: 10,
                        },
                        background: "#f6faff",
                    }}
                >
                    <Container maxWidth="lg">
                        <Grid
                            container
                            spacing={6}
                            alignItems="center"
                        >
                            <Grid
                                item
                                xs={12}
                                md={5}
                            >
                                <Typography
                                    variant="overline"
                                    color="primary.main"
                                    fontWeight={700}
                                    letterSpacing={2}
                                >
                                    The Challenge
                                </Typography>

                                <Typography
                                    variant="h3"
                                    sx={{
                                        mt: 1,
                                        mb: 3,
                                        fontSize: {
                                            xs: "2rem",
                                            md: "2.8rem",
                                        },
                                    }}
                                >
                                    Keeping children engaged
                                    while they learn
                                </Typography>

                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.9,
                                    }}
                                >
                                    Keeping children engaged
                                    while providing meaningful
                                    learning content can be
                                    challenging in today’s
                                    digital environment.
                                </Typography>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={7}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: {
                                            xs: 3,
                                            md: 4,
                                        },
                                        borderRadius: 4,
                                        background: "#fff",
                                        border:
                                            "1px solid #e3edf7",
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        fontWeight={700}
                                        sx={{ mb: 3 }}
                                    >
                                        Parents and educators
                                        need content that can:
                                    </Typography>

                                    <Grid
                                        container
                                        spacing={2}
                                    >
                                        {[
                                            "Capture children’s attention",
                                            "Make learning enjoyable",
                                            "Encourage curiosity and creativity",
                                            "Support learning through audio-visual content",
                                            "Provide meaningful and engaging digital experiences",
                                        ].map((item) => (
                                            <Grid
                                                item
                                                xs={12}
                                                sm={6}
                                                key={item}
                                            >
                                                <Stack
                                                    direction="row"
                                                    spacing={1.5}
                                                    alignItems="flex-start"
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 25,
                                                            height: 25,
                                                            borderRadius:
                                                                "50%",
                                                            background:
                                                                "#e3f2fd",
                                                            color:
                                                                "primary.main",
                                                            display:
                                                                "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            flexShrink: 0,
                                                            mt: 0.2,
                                                        }}
                                                    >
                                                        ✓
                                                    </Box>

                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            lineHeight:
                                                                1.7,
                                                        }}
                                                    >
                                                        {item}
                                                    </Typography>
                                                </Stack>
                                            </Grid>
                                        ))}
                                    </Grid>

                                    <Divider
                                        sx={{ my: 3 }}
                                    />

                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{
                                            lineHeight: 1.8,
                                        }}
                                    >
                                        <strong>
                                            iMPULZ Kids
                                        </strong>{" "}
                                        was designed to address
                                        these needs through a
                                        child-friendly digital
                                        learning platform.
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>

                {/* ==================================================
                    OUR SOLUTION
                ================================================== */}

                <Box
                    sx={{
                        py: {
                            xs: 8,
                            md: 10,
                        },
                        background: "#fff",
                    }}
                >
                    <Container maxWidth="lg">
                        <Grid
                            container
                            spacing={6}
                            alignItems="center"
                        >
                            <Grid
                                item
                                xs={12}
                                md={6}
                                order={{
                                    xs: 2,
                                    md: 1,
                                }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        minHeight: 390,
                                        borderRadius: 5,
                                        p: 4,
                                        background:
                                            "linear-gradient(145deg, #e3f2fd, #fff)",
                                        border:
                                            "1px solid #d8ebfa",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent:
                                            "center",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            textAlign: "center",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display:
                                                    "flex",
                                                justifyContent:
                                                    "center",
                                                mb: 3,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 120,
                                                    height: 120,
                                                    borderRadius:
                                                        "50%",
                                                    background:
                                                        "linear-gradient(135deg, #1976d2, #64b5f6)",
                                                    color: "#fff",
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    justifyContent:
                                                        "center",
                                                    boxShadow:
                                                        "0 15px 35px rgba(25,118,210,0.2)",
                                                }}
                                            >
                                                <ChildCare
                                                    sx={{
                                                        fontSize: 65,
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Typography
                                            variant="h4"
                                            fontWeight={800}
                                            color="primary.main"
                                        >
                                            Learn
                                        </Typography>

                                        <Typography
                                            variant="h4"
                                            fontWeight={800}
                                            color="secondary.main"
                                        >
                                            Play
                                        </Typography>

                                        <Typography
                                            variant="h4"
                                            fontWeight={800}
                                            color="primary.main"
                                        >
                                            Explore
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}
                                order={{
                                    xs: 1,
                                    md: 2,
                                }}
                            >
                                <Typography
                                    variant="overline"
                                    color="primary.main"
                                    fontWeight={700}
                                    letterSpacing={2}
                                >
                                    Our Solution
                                </Typography>

                                <Typography
                                    variant="h3"
                                    sx={{
                                        mt: 1,
                                        mb: 3,
                                        fontSize: {
                                            xs: "2rem",
                                            md: "2.8rem",
                                        },
                                    }}
                                >
                                    Learning and
                                    entertainment together
                                </Typography>

                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.9,
                                        mb: 2,
                                    }}
                                >
                                    Lider Technology Solutions
                                    developed{" "}
                                    <strong>
                                        iMPULZ Kids
                                    </strong>{" "}
                                    as an interactive learning
                                    and entertainment platform
                                    specifically focused on
                                    children.
                                </Typography>

                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.9,
                                    }}
                                >
                                    The application brings
                                    together educational and
                                    entertaining content in an
                                    easy-to-use mobile
                                    environment, allowing
                                    children to{" "}
                                    <strong>
                                        learn, play, watch,
                                        listen, and explore.
                                    </strong>
                                </Typography>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>

                {/* ==================================================
                    KEY FEATURES
                ================================================== */}

                <Box
                    sx={{
                        py: {
                            xs: 8,
                            md: 10,
                        },
                        background: "#f6faff",
                    }}
                >
                    <Container maxWidth="lg">
                        <Box
                            sx={{
                                textAlign: "center",
                                mb: 6,
                            }}
                        >
                            <Typography
                                variant="overline"
                                color="primary.main"
                                fontWeight={700}
                                letterSpacing={2}
                            >
                                Key Features
                            </Typography>

                            <Typography
                                variant="h3"
                                sx={{
                                    mt: 1,
                                    mb: 2,
                                    fontSize: {
                                        xs: "2rem",
                                        md: "2.8rem",
                                    },
                                }}
                            >
                                Designed for young learners
                            </Typography>

                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{
                                    maxWidth: 650,
                                    mx: "auto",
                                    lineHeight: 1.8,
                                }}
                            >
                                iMPULZ Kids combines
                                educational and entertaining
                                experiences to make digital
                                learning more enjoyable.
                            </Typography>
                        </Box>

                        <Grid
                            container
                            spacing={3}
                        >
                            {features.map((feature) => (
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    key={feature.title}
                                >
                                    <FeatureCard
                                        {...feature}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Box>

                {/* ==================================================
                    LEARNING & DEVELOPMENT
                ================================================== */}

                <Box
                    sx={{
                        py: {
                            xs: 8,
                            md: 10,
                        },
                        background: "#fff",
                    }}
                >
                    <Container maxWidth="lg">
                        <Grid
                            container
                            spacing={6}
                            alignItems="center"
                        >
                            <Grid
                                item
                                xs={12}
                                md={5}
                            >
                                <Typography
                                    variant="overline"
                                    color="primary.main"
                                    fontWeight={700}
                                    letterSpacing={2}
                                >
                                    Learning & Development
                                </Typography>

                                <Typography
                                    variant="h3"
                                    sx={{
                                        mt: 1,
                                        mb: 3,
                                        fontSize: {
                                            xs: "2rem",
                                            md: "2.8rem",
                                        },
                                    }}
                                >
                                    Encouraging natural
                                    development
                                </Typography>

                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.9,
                                        mb: 3,
                                    }}
                                >
                                    iMPULZ Kids is designed to
                                    encourage children to
                                    discover, explore, and
                                    develop through engaging
                                    digital experiences.
                                </Typography>

                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.9,
                                    }}
                                >
                                    The platform supports
                                    different aspects of
                                    children’s learning and
                                    development through
                                    stories, rhymes, tutorials,
                                    and interactive content.
                                </Typography>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={7}
                            >
                                <Grid
                                    container
                                    spacing={2}
                                >
                                    {developmentAreas.map(
                                        (item) => (
                                            <Grid
                                                item
                                                xs={12}
                                                sm={6}
                                                key={
                                                    item.title
                                                }
                                            >
                                                <DevelopmentCard
                                                    {...item}
                                                />
                                            </Grid>
                                        )
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>

                {/* ==================================================
                    PROJECT HIGHLIGHTS
                ================================================== */}

                <Box
                    sx={{
                        py: {
                            xs: 8,
                            md: 10,
                        },
                        background: "#f6faff",
                    }}
                >
                    <Container maxWidth="lg">
                        <Box
                            sx={{
                                textAlign: "center",
                                mb: 6,
                            }}
                        >
                            <Typography
                                variant="overline"
                                color="primary.main"
                                fontWeight={700}
                                letterSpacing={2}
                            >
                                Project Highlights
                            </Typography>

                            <Typography
                                variant="h3"
                                sx={{
                                    mt: 1,
                                    fontSize: {
                                        xs: "2rem",
                                        md: "2.8rem",
                                    },
                                }}
                            >
                                iMPULZ Kids at a glance
                            </Typography>
                        </Box>

                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 4,
                                overflow: "hidden",
                                border:
                                    "1px solid #e3edf7",
                            }}
                        >
                            {projectDetails.map(
                                (item, index) => (
                                    <Box
                                        key={item.label}
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: {
                                                xs: "1fr",
                                                sm: "1fr 2fr",
                                            },
                                            borderBottom:
                                                index !==
                                                projectDetails.length -
                                                    1
                                                    ? "1px solid #e9eef4"
                                                    : "none",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                p: 2.5,
                                                background:
                                                    "#f5f9fd",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                fontWeight={700}
                                                color="primary.main"
                                            >
                                                {item.label}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                p: 2.5,
                                                background:
                                                    "#fff",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {item.value}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )
                            )}
                        </Paper>
                    </Container>
                </Box>

                {/* ==================================================
                    OUR APPROACH
                ================================================== */}

                <Box
                    sx={{
                        py: {
                            xs: 8,
                            md: 10,
                        },
                        background: "#fff",
                    }}
                >
                    <Container maxWidth="lg">
                        <Grid
                            container
                            spacing={6}
                            alignItems="center"
                        >
                            <Grid
                                item
                                xs={12}
                                md={6}
                            >
                                <Typography
                                    variant="overline"
                                    color="primary.main"
                                    fontWeight={700}
                                    letterSpacing={2}
                                >
                                    Our Approach
                                </Typography>

                                <Typography
                                    variant="h3"
                                    sx={{
                                        mt: 1,
                                        mb: 3,
                                        fontSize: {
                                            xs: "2rem",
                                            md: "2.8rem",
                                        },
                                    }}
                                >
                                    Technology that makes
                                    learning meaningful
                                </Typography>

                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.9,
                                        mb: 2,
                                    }}
                                >
                                    At{" "}
                                    <strong>
                                        Lider Technology
                                        Solutions
                                    </strong>
                                    , we believe technology
                                    can transform the way
                                    children learn and interact
                                    with educational content.
                                </Typography>

                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.9,
                                        mb: 2,
                                    }}
                                >
                                    With iMPULZ Kids, our focus
                                    is to create a{" "}
                                    <strong>
                                        fun, engaging, and
                                        educational digital
                                        environment
                                    </strong>{" "}
                                    where children can discover
                                    new concepts through
                                    content that captures
                                    their natural curiosity
                                    and attention.
                                </Typography>

                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.9,
                                    }}
                                >
                                    The platform brings{" "}
                                    <strong>
                                        learning and
                                        entertainment together
                                    </strong>{" "}
                                    to create meaningful
                                    digital experiences for
                                    young learners.
                                </Typography>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: {
                                            xs: 3,
                                            md: 5,
                                        },
                                        borderRadius: 5,
                                        background:
                                            "linear-gradient(135deg, #1976d2, #42a5f5)",
                                        color: "#fff",
                                        position:
                                            "relative",
                                        overflow: "hidden",
                                    }}
                                >
                                    <Star
                                        sx={{
                                            position:
                                                "absolute",
                                            fontSize: 150,
                                            opacity: 0.08,
                                            right: -25,
                                            top: -25,
                                        }}
                                    />

                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        sx={{
                                            mb: 3,
                                            position:
                                                "relative",
                                        }}
                                    >
                                        Learn. Play. Explore.
                                    </Typography>

                                    <Stack
                                        spacing={2.5}
                                        sx={{
                                            position:
                                                "relative",
                                        }}
                                    >
                                        {[
                                            {
                                                icon: (
                                                    <School />
                                                ),
                                                title: "Learn",
                                                text: "Discover concepts through engaging educational content.",
                                            },
                                            {
                                                icon: (
                                                    <Toys />
                                                ),
                                                title: "Play",
                                                text: "Make learning enjoyable through entertainment and activities.",
                                            },
                                            {
                                                icon: (
                                                    <Explore />
                                                ),
                                                title: "Explore",
                                                text: "Encourage curiosity and creativity through discovery.",
                                            },
                                        ].map((item) => (
                                            <Stack
                                                key={
                                                    item.title
                                                }
                                                direction="row"
                                                spacing={2}
                                                alignItems="center"
                                            >
                                                <Box
                                                    sx={{
                                                        width: 48,
                                                        height: 48,
                                                        borderRadius:
                                                            3,
                                                        background:
                                                            "rgba(255,255,255,0.15)",
                                                        display:
                                                            "flex",
                                                        alignItems:
                                                            "center",
                                                        justifyContent:
                                                            "center",
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {
                                                        item.icon
                                                    }
                                                </Box>

                                                <Box>
                                                    <Typography
                                                        variant="h6"
                                                        fontWeight={
                                                            700
                                                        }
                                                    >
                                                        {
                                                            item.title
                                                        }
                                                    </Typography>

                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            opacity:
                                                                0.9,
                                                        }}
                                                    >
                                                        {
                                                            item.text
                                                        }
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>

                {/* ==================================================
                    VISION
                ================================================== */}

                <Box
                    sx={{
                        py: {
                            xs: 9,
                            md: 11,
                        },
                        background:
                            "linear-gradient(135deg, #0d47a1 0%, #1976d2 55%, #42a5f5 100%)",
                        color: "#fff",
                        textAlign: "center",
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    <Container
                        maxWidth="md"
                        sx={{
                            position: "relative",
                            zIndex: 1,
                        }}
                    >
                        <Typography
                            variant="overline"
                            sx={{
                                letterSpacing: 3,
                                opacity: 0.85,
                            }}
                        >
                            Vision
                        </Typography>

                        <Typography
                            variant="h3"
                            sx={{
                                mt: 2,
                                mb: 3,
                                fontSize: {
                                    xs: "2rem",
                                    md: "3rem",
                                },
                            }}
                        >
                            A joyful digital learning
                            environment for every child
                        </Typography>

                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 400,
                                lineHeight: 1.8,
                                opacity: 0.92,
                                mb: 4,
                            }}
                        >
                            iMPULZ Kids aims to create an
                            engaging digital learning
                            environment where children can
                            learn with curiosity, play with
                            creativity, and explore through
                            interactive content.
                        </Typography>

                        <Divider
                            sx={{
                                borderColor:
                                    "rgba(255,255,255,0.25)",
                                mb: 3,
                            }}
                        />

                        <Typography
                            variant="h5"
                            fontWeight={800}
                        >
                            iMPULZ Kids — Learn. Play. Explore.
                        </Typography>
                    </Container>
                </Box>

                {/* ==================================================
                    FOOTER
                ================================================== */}

                <Footer />
            </ThemeProvider>
        </>
    );
}